import { useEffect, useState, useCallback } from "react";
import { promotionsAPI } from "../services/api";
import { Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { format } from "date-fns";

const blank = { code:"", type:"percentage", value:"", min_order:"0", max_uses:"", expires_at:"" };

export default function Promotions() {
  const [promos, setPromos]   = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [modal, setModal]     = useState(false);
  const [form, setForm]       = useState(blank);
  const [saving, setSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await promotionsAPI.list({ page, limit: 15 });
      setPromos(r.data.data); setTotal(r.data.pagination?.total||0);
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  }, [page]);

  useEffect(()=>{ load(); },[load]);

  const handleCreate = async () => {
    setSaving(true);
    try {
      await promotionsAPI.create(form);
      toast.success("Promotion created!");
      setModal(false); setForm(blank); load();
    } catch (e) { toast.error(e.response?.data?.message||"Failed"); }
    finally { setSaving(false); }
  };

  const handleToggle = async (p) => {
    try {
      await promotionsAPI.update(p.id, { ...p, is_active: p.is_active ? 0 : 1 });
      toast.success(p.is_active ? "Deactivated" : "Activated");
      load();
    } catch { toast.error("Failed"); }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Deactivate code "${p.code}"?`)) return;
    try { await promotionsAPI.delete(p.id); toast.success("Done"); load(); }
    catch { toast.error("Failed"); }
  };

  const pages = Math.ceil(total/15);

  return (
    <div>
      <style>{`
        .promo-mobile-cards  { display: none; }
        .promo-desktop-table { display: block; }
        .promo-card {
          padding: 14px 16px;
          border-bottom: 1px solid var(--border);
        }
        .promo-card:last-child { border-bottom: none; }
        @media (max-width: 768px) {
          .promo-mobile-cards  { display: block; }
          .promo-desktop-table { display: none; }
        }
      `}</style>

      <div className="page-header">
        <div>
          <div className="page-title">Promotions</div>
          <div className="page-subtitle">{total} promo codes</div>
        </div>
        <button className="btn btn-primary" onClick={()=>setModal(true)}><Plus size={15}/> New Promo</button>
      </div>

      <div className="card">
        {loading ? <div className="loading-center"><div className="spinner"/><span>Loading...</span></div> : (
          <>
            {/* Mobile cards */}
            <div className="promo-mobile-cards">
              {promos.length===0
                ? <div className="empty-state"><p>No promotions yet</p></div>
                : promos.map(p=>(
                <div key={p.id} className="promo-card">
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                    <div>
                      <span className="mono" style={{color:"var(--accent)",fontWeight:700,fontSize:14}}>{p.code}</span>
                      <span className={`badge ${p.is_active?"badge-active":"badge-suspended"}`} style={{marginLeft:8}}>{p.is_active?"Active":"Inactive"}</span>
                    </div>
                    <span className="fw-bold">
                      {p.type==="percentage"?`${p.value}%`:p.type==="fixed"?`GHS ${p.value}`:"Free"}
                    </span>
                  </div>
                  <div style={{fontSize:12,color:"var(--text3)",marginBottom:8,display:"flex",gap:12,flexWrap:"wrap"}}>
                    <span style={{textTransform:"capitalize"}}>{p.type.replace(/_/g," ")}</span>
                    <span>Min: GHS {p.min_order}</span>
                    <span>Used {p.used_count}/{p.max_uses||"∞"}</span>
                    <span>{p.expires_at?format(new Date(p.expires_at),"MMM d, yyyy"):"No expiry"}</span>
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <button className={`btn btn-sm ${p.is_active?"btn-ghost":"btn-success"}`} onClick={()=>handleToggle(p)}>{p.is_active?"Pause":"Activate"}</button>
                    <button className="btn btn-danger btn-icon btn-sm" onClick={()=>handleDelete(p)}><Trash2 size={13}/></button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="promo-desktop-table table-wrap">
              <table>
                <thead><tr><th>Code</th><th>Type</th><th>Value</th><th>Min Order</th><th>Used</th><th>Max Uses</th><th>Expires</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {promos.length===0
                    ? <tr><td colSpan={9}><div className="empty-state"><p>No promotions yet</p></div></td></tr>
                    : promos.map(p=>(
                    <tr key={p.id}>
                      <td><span className="mono" style={{color:"var(--accent)",fontWeight:700,fontSize:13}}>{p.code}</span></td>
                      <td style={{textTransform:"capitalize",color:"var(--text2)"}}>{p.type.replace(/_/g," ")}</td>
                      <td className="fw-bold">{p.type==="percentage"?`${p.value}%`:p.type==="fixed"?`GHS ${p.value}`:"Free"}</td>
                      <td className="mono">GHS {p.min_order}</td>
                      <td className="mono">{p.used_count}</td>
                      <td className="mono">{p.max_uses||"∞"}</td>
                      <td className="mono" style={{fontSize:11,color:"var(--text3)"}}>{p.expires_at?format(new Date(p.expires_at),"MMM d, yyyy"):"Never"}</td>
                      <td><span className={`badge ${p.is_active?"badge-active":"badge-suspended"}`}>{p.is_active?"Active":"Inactive"}</span></td>
                      <td>
                        <div style={{display:"flex",gap:4}}>
                          <button className={`btn btn-sm ${p.is_active?"btn-ghost":"btn-success"}`} onClick={()=>handleToggle(p)}>{p.is_active?"Pause":"Activate"}</button>
                          <button className="btn btn-danger btn-icon btn-sm" onClick={()=>handleDelete(p)}><Trash2 size={13}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <span>Showing {promos.length} of {total}</span>
              <div className="pagination-btns">
                <button className="btn btn-ghost btn-sm" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>Prev</button>
                <span style={{padding:"0 8px",color:"var(--text2)"}}>Page {page} of {pages||1}</span>
                <button className="btn btn-ghost btn-sm" disabled={page>=pages} onClick={()=>setPage(p=>p+1)}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={()=>setModal(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">New Promotion</div>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={()=>setModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Promo Code</label>
                  <input className="input" placeholder="WELCOME10" value={form.code} onChange={e=>setForm(f=>({...f,code:e.target.value.toUpperCase()}))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Type</label>
                  <select className="select" value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))}>
                    <option value="percentage">Percentage %</option>
                    <option value="fixed">Fixed Amount</option>
                    <option value="free_delivery">Free Delivery</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Value {form.type==="percentage"?"(%)":"(GHS)"}</label>
                  <input className="input" type="number" value={form.value} onChange={e=>setForm(f=>({...f,value:e.target.value}))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Min Order (GHS)</label>
                  <input className="input" type="number" value={form.min_order} onChange={e=>setForm(f=>({...f,min_order:e.target.value}))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Max Uses (blank = unlimited)</label>
                  <input className="input" type="number" placeholder="100" value={form.max_uses} onChange={e=>setForm(f=>({...f,max_uses:e.target.value}))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Expires At</label>
                  <input className="input" type="datetime-local" value={form.expires_at} onChange={e=>setForm(f=>({...f,expires_at:e.target.value}))} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={saving||!form.code||!form.value}>{saving?"Creating...":"Create Promo"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}